import { FormInstance } from "antd";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import useCoreStore from "views/hooks/useCoreStore";
import useProductApi from "./useProductApi";
import useCurrencyApi from "views/Settings/General/components/Formats/Currency/hooks/useCurrencyApi";

interface UseProductFormDataParams {
  form: FormInstance;
  isOpen: boolean;
  productId?: ID;
}

export default function useProductFormData({ form, isOpen, productId }: UseProductFormDataParams) {
  const urlParams: RouteParams = useParams();
  const companyData = useCoreStore(state => state.companyData);
  const companySettings = useCoreStore(state => state.companySettings);

  const counterpartProductAllocationType = companySettings?.counterpartProductAllocationType?.value;
  const counterpartByCategory = counterpartProductAllocationType === "by_category";
  const decimalPlaces = companySettings?.decimalPlaces;

  const hasDeclareTva = !companyData?.fiscal || companyData?.fiscal?.declareTva;

  const {
    listCategories,
    categoryOptions,
    refetchCategories,
    listProductOperationalCategories,
    operationsCategories,
    getProduct,
    refetchGetProduct,
    formData,
  } = useProductApi(productId);

  const { listCurrenciesOptions, currenciesOption } = useCurrencyApi();

  useEffect(() => {
    if (urlParams?.companyId && isOpen) {
      listCategories({
        variables: {
          filter: { company: urlParams.companyId },
        },
      });
      listCurrenciesOptions({
        variables: {
          filter: { companyId: urlParams?.companyId, isActive: true },
        },
      });
      listProductOperationalCategories();
    }
  }, [urlParams?.companyId, isOpen]);

  useEffect(() => {
    if (productId && isOpen) {
      getProduct({ variables: { id: productId } });
    }
  }, [productId, isOpen]);

  useEffect(() => {
    if (currenciesOption?.length > 0 && isOpen && !productId) {
      const currentPrices = form.getFieldValue("prices");

      if (!currentPrices || currentPrices.length === 0) {
        const defaultPrices = currenciesOption.map(item => ({
          currency: item?.value,
          extraInfo: {
            currencyCode: item?.label,
            decimalPlaces: item?.decimalPlaces,
            isMainCurrency: item?.isMainCurrency,
          },
          buyPriceTotal: undefined,
          priceNet: undefined,
          priceIncludingVat: undefined,
        }));

        form.setFieldValue("prices", defaultPrices);
      }
    }
  }, [currenciesOption, isOpen, productId, form]);

  useEffect(() => {
    if (formData && productId) {
      const pricesData = formData.prices?.map(item => {
        const priceIncludingVat =
          Number(
            (Number(item?.currency?.priceNet?.net) * Number(formData?.vat?.rate)).toFixed(
              decimalPlaces,
            ),
          ) + Number(item?.currency?.priceNet?.net);

        return {
          productId,
          currency: item?.currency?.code,
          extraInfo: {
            currencyCode: item?.currency?.code,
            decimalPlaces: item?.currency?.decimalPlaces,
            isMainCurrency: item?.currency?.isMainCurrency,
          },
          buyPriceTotal: item?.currency?.buyPrice?.total,
          priceNet: item?.currency?.priceNet?.net,
          priceIncludingVat,
        };
      });

      const languages = ["fr", "en", "es"] as const;
      const translationsData = languages.map(lang => {
        const nameTranslation = formData?.nameTranslations?.find(
          (t: { language: string; value: string }) => t.language === lang,
        );
        const descTranslation = formData?.descriptionTranslations?.find(
          (t: { language: string; value: string }) => t.language === lang,
        );
        return {
          language: lang,
          name: nameTranslation?.value || "",
          description: descTranslation?.value || "",
        };
      });

      form.setFields([
        { name: "productId", value: formData?.id },
        { name: "category", value: formData?.category?.id },
        { name: "operationalCategory", value: formData?.operationalCategory?.value },
        { name: "reference", value: formData?.reference },
        { name: "translations", value: translationsData },
        {
          name: "vat",
          value: {
            label: `${formData?.vat?.code} - ${formData?.vat?.name} - ${formData?.vat?.ratePercentage} %`,
            value: formData.vat?.id,
            rate: formData?.vat?.rate,
          },
        },
        {
          name: "counterpartCode",
          value: {
            label: formData?.counterpartCode,
            value: formData?.counterpartCode,
          },
        },
        {
          name: "productType",
          value: formData?.productType && {
            label: `${formData?.productType?.code} - ${formData?.productType?.name}`,
            value: formData.productType?.id,
          },
        },
        {
          name: "unitMeasure",
          value: formData?.unitMeasure && {
            label: `${formData?.unitMeasure?.name} - ${formData?.unitMeasure?.slug}`,
            value: formData.unitMeasure?.id,
          },
        },
        { name: "prices", value: pricesData },
      ]);
    }
  }, [formData, productId, decimalPlaces, form]);

  const onValuesChange = (changedValues: any, allValues: any) => {
    if (changedValues?.prices) {
      const vat = form.getFieldValue("vat");
      const index = Object.keys(changedValues.prices)?.[0];

      if (index && allValues?.prices?.[Number(index)]) {
        const priceNet = allValues.prices[Number(index)]?.priceNet;

        if (vat?.rate && !isNaN(priceNet)) {
          const priceIncludingVat =
            Number((Number(priceNet) * Number(vat.rate)).toFixed(decimalPlaces)) + Number(priceNet);

          form.setFieldValue(["prices", Number(index), "priceIncludingVat"], priceIncludingVat);
        }
      }
    }
  };

  return {
    categoryOptions,
    operationsCategories,
    hasDeclareTva,
    counterpartByCategory,
    refetchCategories,
    refetchGetProduct,
    onValuesChange,
  };
}
